// Scored adapter registry + tier policy (Phase 1a).
//
// Lockstep with evals/adapters/_registry.py. The pipeline polls every
// registered adapter's identify() against one shared Sample, ranks candidates
// by score, and applies the tier policy (spec §3–§4):
//
//   high                          -> auto    (route, no confirm)
//   low / top-two within a margin -> confirm (show guess + runner-up)
//   none                          -> recover (explicit "unrecognized" state)
//
// The result is a structured Classification, not a verdict. No model calls —
// deterministic + local (the on-abstain model classifier is a later phase).

import type { Sample } from "./_contract";
import {
  scoreAppclose,
  scoreCivilCommunicator,
  scoreGenericCsv,
  scoreOfw,
  scoreOfwCsv,
  scoreTalkingParents,
  scoreTwoHouses,
} from "./_fingerprint";

// Per-format margin: when the top two candidates' scores are within this band
// the call is "close" and routes to confirm even if the leader cleared its
// auto threshold (abstain on close calls). Lockstep with DEFAULT_MARGIN.
export const DEFAULT_MARGIN = 0.15;

export type Tier = "auto" | "confirm" | "recover";

export interface AdapterSpec {
  name: string;
  identify: (sample: Sample) => number;
  autoThreshold: number;
}

// Civil Communicator is distinctive → high bar (0.80). OFW variants overlap a
// touch but the repeating-record backbone is decisive → 0.75. Generic CSV is a
// pure header match → only the full canonical schema (1.0) auto-routes.
// Lockstep with DETECTION_REGISTRY.
export const DETECTION_REGISTRY: AdapterSpec[] = [
  { name: "civil-communicator", identify: scoreCivilCommunicator, autoThreshold: 0.8 },
  // 2houses is distinctive (banner + localized " h " timestamp) → high bar.
  { name: "two-houses", identify: scoreTwoHouses, autoThreshold: 0.8 },
  // TalkingParents is distinctive (certified footer + "… said:" delimiter)
  // → high bar.
  { name: "talking-parents", identify: scoreTalkingParents, autoThreshold: 0.8 },
  // AppClose ships NO certification block (unlike TalkingParents), but its
  // running "AppClose Records Export" header plus the read-receipt-bearing
  // record grammar are just as distinctive → high bar.
  { name: "appclose", identify: scoreAppclose, autoThreshold: 0.8 },
  { name: "ofw", identify: scoreOfw, autoThreshold: 0.75 },
  // OFW message CSV (Date,Time,Sender,Message) is a KNOWN, distinctive shape →
  // exact signature scores 1.0 and auto-routes (Phase 3, Piece A). Listed ahead
  // of generic-csv so it wins the tie on a partial header overlap.
  { name: "ofw-csv", identify: scoreOfwCsv, autoThreshold: 1.0 },
  { name: "generic-csv", identify: scoreGenericCsv, autoThreshold: 1.0 },
];

export interface Candidate {
  name: string;
  score: number;
  autoThreshold: number;
}

export function clearedAuto(c: Candidate): boolean {
  return c.score >= c.autoThreshold;
}

export interface Classification {
  tier: Tier;
  best: Candidate | null;
  runnerUp: Candidate | null;
  candidates: Candidate[];
  margin: number;
  /** The auto-routed format, or null when tier !== "auto". */
  format: string | null;
}

/**
 * Score every registered adapter against `sample`, ranked descending. Ties
 * break on registry order (stable sort). identify() must not throw; a throwing
 * scorer is treated as score 0 so one bad adapter can't break detection.
 */
export function poll(
  sample: Sample,
  registry: AdapterSpec[] = DETECTION_REGISTRY,
): Candidate[] {
  const cands: Candidate[] = registry.map((spec) => {
    let score: number;
    try {
      score = spec.identify(sample);
    } catch {
      score = 0.0;
    }
    if (!Number.isFinite(score)) score = 0.0;
    score = Math.max(0.0, Math.min(1.0, score));
    return { name: spec.name, score, autoThreshold: spec.autoThreshold };
  });
  // Stable sort by descending score (registry order preserved on ties).
  return cands
    .map((c, i) => [c, i] as const)
    .sort((a, b) => (b[0].score - a[0].score) || (a[1] - b[1]))
    .map(([c]) => c);
}

/** Apply the tier policy to a ranked candidate list. Lockstep with classify(). */
export function classify(
  candidates: Candidate[],
  margin: number = DEFAULT_MARGIN,
): Classification {
  const scored = candidates.filter((c) => c.score > 0.0);
  if (scored.length === 0) {
    return {
      tier: "recover",
      best: null,
      runnerUp: null,
      candidates,
      margin,
      format: null,
    };
  }

  const best = scored[0];
  const runnerUp = scored.length > 1 ? scored[1] : null;

  let tier: Tier;
  if (!clearedAuto(best)) {
    tier = "confirm";
  } else if (runnerUp !== null && best.score - runnerUp.score <= margin) {
    // Two plausible formats too close to call — confirm, show both.
    tier = "confirm";
  } else {
    tier = "auto";
  }

  return {
    tier,
    best,
    runnerUp,
    candidates,
    margin,
    format: tier === "auto" ? best.name : null,
  };
}

/** Poll + classify in one call — the pipeline's detection entry point. */
export function detect(
  sample: Sample,
  registry: AdapterSpec[] = DETECTION_REGISTRY,
  margin: number = DEFAULT_MARGIN,
): Classification {
  return classify(poll(sample, registry), margin);
}
